import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Datatable} from '../../../../components/Datatable/Datatable'
import type {MetaType} from '../../../../components/Datatable/Datatable'
import {Button} from '../../../../components/Button/Button'
import {Input} from '../../../../components/Input/Input'
import {ModalWrapper} from '../../../../components/Modals/General/ModalWrapper/ModalWrapper'
import useModals from '../../../../hooks/useModals'
import {GET, POST, PUT} from '../../../../services/api'
import {Loading} from '../../../../components/Loading/Loading'
import Swal from 'sweetalert2'
import {i18n} from '../../../../translate/i18n'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import {toDate} from '../../../../utils/formater'
import * as XLSX from 'sheetjs-style'

// Types
export enum TypeReservationBooking {
  TOUR = 'tour',
  EXPERIENCE = 'experience',
  ACTIVITY = 'activity',
  ROOMSERVICE = 'roomService',
}

export enum TypeReservationRoomStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
}

interface StatusHotel {
  id: number
  pluginspaceId: number
  plugintypeId: number
  name: string
  slug: string
  order: number
  messageStatus: string
  status: boolean
  activeEmail: boolean
}

interface FilterReservationBookingDto {
  pluginspaceId?: number
  reservationId?: number
  id?: number
  itemType?: TypeReservationBooking
  itemId?: number
  status?: TypeReservationRoomStatus
  statusHotelId?: number
  startDate?: string
  endDate?: string
  minParticipants?: number
  maxParticipants?: number
  minPrice?: number
  maxPrice?: number
  currency?: string
  address?: string
}

interface ReservationBooking {
  id: number
  pluginspaceId: number
  reservationId: number
  itemType: TypeReservationBooking
  itemId: number
  status: TypeReservationRoomStatus
  statusHotelId?: number | null
  statusHotel?: StatusHotel | null
  reservationDate: string
  participants: number
  totalAmount: number
  currency: string
  specialRequests?: string
  guestNotes?: string
  address?: string
  content?: any
  cancelled?: string
  cancellationReason?: string
  created: string
  updated: string
  // Dados relacionados que vêm da API
  hotelReservation?: any
  hotelActivity?: any
  hotelExperience?: any
  hotelTour?: any
  item?: any
}

const HotelReservesContent: FC = () => {
  const {showWrapperModal, closeWrapperModal} = useModals()
  const [loading, setLoading] = useState(false)
  const [reservationBookings, setReservationBookings] = useState<ReservationBooking[]>([])
  const [meta, setMeta] = useState<MetaType | undefined>(undefined)
  const [filters, setFilters] = useState<FilterReservationBookingDto>({})
  const [selectedReservation, setSelectedReservation] = useState<ReservationBooking | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [loadingCancel, setLoadingCancel] = useState(false)
  const [loadingStatusChange] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [modalType, setModalType] = useState<'details' | 'cancel' | 'status' | null>(null)
  const [loadingExport, setLoadingExport] = useState(false)
  const [statusHotelOptions, setStatusHotelOptions] = useState<StatusHotel[]>([])

  const getStatusHotel = async () => {
    try {
      const res = await POST(`/status-hotel/filter`, {
        status: true,
      })
      const statusList = Array.isArray(res.data) ? res.data : []
      setStatusHotelOptions(statusList)
      return statusList
    } catch (e) {
      console.error(e)
      setStatusHotelOptions([])
      return []
    }
  }

  const getReservationBookings = async (searchFilters?: FilterReservationBookingDto) => {
    try {
      setLoading(true)

      const filtersToUse = searchFilters || filters

      const res = await POST('/reservation-booking/filter', filtersToUse)

      setReservationBookings(res.data || [])
      setMeta(res.meta)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao carregar as reservas!',
      })
    }
  }

  const handleFilterChange = (field: keyof FilterReservationBookingDto, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSearch = () => {
    getReservationBookings(filters)
  }

  const handleClearFilters = () => {
    setFilters({})
    getReservationBookings({})
  }

  function getTitle(item: ReservationBooking) {
    switch (item.itemType) {
      case TypeReservationBooking.TOUR:
        return item.hotelTour?.title || '-'
      case TypeReservationBooking.EXPERIENCE:
        return item.hotelExperience?.title || '-'
      case TypeReservationBooking.ACTIVITY:
        return item.hotelActivity?.title || '-'
      case TypeReservationBooking.ROOMSERVICE:
        return `Serviço de Quarto #${item.itemId}`
      default:
        return '-'
    }
  }

  const formatReservationBookingData = (data: ReservationBooking[]) => {
    return data.map((item) => ({
      ...item,
      title: getTitle(item),
      itemTypeLabel: getItemTypeLabel(item.itemType),
      statusLabel:
        item.statusHotel?.name ||
        statusHotelOptions.find((status) => status.id === item.statusHotelId)?.name ||
        getStatusLabel(item.status),
      totalAmountFormatted: item.totalAmount ? `${item.currency} ${item.totalAmount}` : '-',
      reservationDateFormatted: new Date(item.reservationDate).toLocaleString('pt-BR'),
      created: new Date(item.created).toLocaleString('pt-BR'),
      participantsText: `${item.participants} pessoa${item.participants > 1 ? 's' : ''}`,
      customerName: item.hotelReservation?.customer?.name || '-',
      customerEmail: item.hotelReservation?.customer?.email || '-',
      customerDoc:
        item.hotelReservation?.customer?.cpf || item.hotelReservation?.customer?.doc || '-',
      roomId: item.hotelReservation?.roomId || '-',
    }))
  }

  const getItemTypeLabel = (type: TypeReservationBooking) => {
    const labels = {
      [TypeReservationBooking.TOUR]: 'Tour',
      [TypeReservationBooking.EXPERIENCE]: 'Experiência',
      [TypeReservationBooking.ACTIVITY]: 'Atividade',
      [TypeReservationBooking.ROOMSERVICE]: 'Serviço de Quarto',
    }
    return labels[type] || type
  }

  const getStatusLabel = (status: TypeReservationRoomStatus) => {
    const labels = {
      [TypeReservationRoomStatus.PENDING]: 'Pendente',
      [TypeReservationRoomStatus.CONFIRMED]: 'Confirmado',
      [TypeReservationRoomStatus.CANCELLED]: 'Cancelado',
      [TypeReservationRoomStatus.COMPLETED]: 'Concluído',
    }
    return labels[status] || status
  }

  const getStatusBadgeClass = (statusSlug?: string) => {
    const classes = {
      pending: 'bg-warning',
      confirmed: 'bg-success',
      canceled: 'bg-danger',
      cancelled: 'bg-danger',
      completed: 'bg-info',
      'in-progress': 'bg-primary',
      in_progress: 'bg-primary',
      attending: 'bg-primary',
      ready: 'bg-info',
    }
    return classes[(statusSlug || '') as keyof typeof classes] || 'bg-secondary'
  }

  const statusOptions = statusHotelOptions.map((status) => ({
    value: status.slug,
    select: status.name,
  }))

  const selectedStatusSlug =
    statusHotelOptions.find((option) => option.id === filters.statusHotelId)?.slug || ''

  const getReservationStatusName = (reservation: ReservationBooking) =>
    reservation.statusHotel?.name ||
    statusHotelOptions.find((status) => status.id === reservation.statusHotelId)?.name ||
    getStatusLabel(reservation.status)

  const getReservationStatusSlug = (reservation: ReservationBooking) =>
    reservation.statusHotel?.slug ||
    statusHotelOptions.find((status) => status.id === reservation.statusHotelId)?.slug ||
    reservation.status

  const isReservationCancelled = (reservation: ReservationBooking) => {
    const slug = getReservationStatusSlug(reservation)
    return (
      slug === TypeReservationRoomStatus.CANCELLED ||
      slug === 'canceled' ||
      reservation.status === TypeReservationRoomStatus.CANCELLED
    )
  }

  // Função para buscar detalhes da reserva
  const getReservationDetails = async (reservationId: number) => {
    try {
      setLoadingDetails(true)
      const res = await GET(`/reservation-booking/${reservationId}`)
      setSelectedReservation(res.data)
      setModalType('details')

      showWrapperModal('Detalhes da Reserva')

      setLoadingDetails(false)
    } catch (e) {
      setLoadingDetails(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao carregar detalhes da reserva!',
      })
    }
  }

  // Função para cancelar reserva
  const cancelReservation = async () => {
    if (!selectedReservation || !cancelReason.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: 'Por favor, selecione um motivo para o cancelamento.',
      })
      return
    }

    try {
      setLoadingCancel(true)

      await PUT(`/reservation-booking/${selectedReservation.id}/cancel`, {
        cancellationReason: cancelReason,
        reservationId: selectedReservation.reservationId,
      })

      Swal.fire({
        icon: 'success',
        title: 'Sucesso',
        text: 'Reserva cancelada com sucesso!',
      })

      // Atualiza a lista de reservas
      getReservationBookings(filters)

      // Fecha o modal
      closeWrapperModal()
      setCancelReason('')
      setSelectedReservation(null)
      setModalType(null)

      setLoadingCancel(false)
    } catch (e) {
      setLoadingCancel(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao cancelar a reserva!',
      })
    }
  }

  // Função para mostrar modal de cancelamento
  const showCancelModal = (reservation: ReservationBooking) => {
    setSelectedReservation(reservation)
    setCancelReason('')
    setModalType('cancel')

    showWrapperModal('Cancelar Reserva')
  }

  // Função para alterar status
  const changeReservationStatus = async () => {
    if (!selectedReservation || !newStatus) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: 'Por favor, selecione um status.',
      })
      return
    }

    try {
      setLoading(true)

      await PUT(`/reservation-booking/${selectedReservation.id}`, {
        statusHotelId: statusHotelOptions.find((status) => status.slug === newStatus)?.id,
      })

      Swal.fire({
        icon: 'success',
        title: 'Sucesso',
        text: 'Status alterado com sucesso!',
      })

      getReservationBookings(filters)

      closeWrapperModal()
      setNewStatus('')
      setSelectedReservation(null)
      setModalType(null)

      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao alterar o status da reserva!',
      })
    }
  }

  // Função para mostrar modal de alteração de status
  const showStatusModal = (reservation: ReservationBooking) => {
    setSelectedReservation(reservation)
    setNewStatus('')
    setModalType('status')

    showWrapperModal('Alterar Status')
  }

  // Motivos pré-definidos por tipo
  const reasonsByType = {
    tour: [
      'Mudança de planos',
      'Problemas climáticos',
      'Conflito de horário',
      'Motivos de saúde',
      'Cancelamento do hotel',
    ],
    experience: [
      'Mudança de data da estadia',
      'Ocasião especial cancelada',
      'Conflito de agenda',
      'Motivos pessoais',
      'Preferência por outro serviço',
    ],
    activity: [
      'Não me sinto preparado(a)',
      'Conflito de horário',
      'Problemas de saúde',
      'Mudança de planos',
      'Atividade não atende expectativa',
    ],
    roomService: [
      'Mudança de planos',
      'Serviço não necessário',
      'Conflito de horário',
      'Motivos pessoais',
      'Preferência por outro serviço',
    ],
  }

  const typeLabels = {
    tour: 'Passeio',
    experience: 'Experiência',
    activity: 'Atividade',
    roomService: 'Serviço de Quarto',
  }

  // Função para renderizar detalhes específicos por tipo
  const renderItemDetails = (reservation: ReservationBooking) => {
    const {itemType} = reservation
    let itemData = null
    let contentData = null

    // Parse do content JSON se existir
    try {
      contentData = reservation.content ? JSON.parse(reservation.content) : null
    } catch (e) {
      contentData = null
    }

    switch (itemType) {
      case TypeReservationBooking.TOUR:
        itemData = reservation.hotelTour
        return (
          <div className='border rounded p-3 bg-light mb-3'>
            <h6 className='fw-bold text-primary mb-3'>
              <i className='fas fa-route me-2'></i>Detalhes do Passeio
            </h6>
            {itemData && (
              <div className='row'>
                <div className='col-md-6'>
                  <div className='mb-2'>
                    <strong>Título:</strong> {itemData.title}
                  </div>
                  <div className='mb-2'>
                    <strong>Duração:</strong> {itemData.duration}
                  </div>
                  <div className='mb-2'>
                    <strong>Dificuldade:</strong> {itemData.difficulty}
                  </div>
                  {/* <div className='mb-2'>
                    <strong>Rating:</strong> ⭐ {itemData.rating}
                  </div> */}
                  <div className='mb-2'>
                    <strong>Max Participantes:</strong> {itemData.maxParticipants}
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='mb-2'>
                    <strong>Min Participantes:</strong> {itemData.minParticipants}
                  </div>
                  <div className='mb-2'>
                    <strong>Restrição de Idade:</strong>{' '}
                    {itemData.ageRestriction || 'Sem restrições'}
                  </div>
                  <div className='mb-2'>
                    <strong>Inclui Transfer:</strong> {itemData.includesTransfer ? 'Sim' : 'Não'}
                  </div>
                  <div className='mb-2'>
                    <strong>Dependente do Clima:</strong>{' '}
                    {itemData.weatherDependent ? 'Sim' : 'Não'}
                  </div>
                  <div className='mb-2'>
                    <strong>Cancelamento:</strong> {itemData.cancellationHours}h antes
                  </div>
                </div>
              </div>
            )}
            {/* <div className='mb-2'>
              <strong>Descrição:</strong> {itemData?.description}
            </div> */}
            {itemData?.physicalRequirements && (
              <div className='mb-2'>
                <strong>Requisitos Físicos:</strong> {itemData.physicalRequirements}
              </div>
            )}

            {contentData && (
              <div className='mt-3 p-2 bg-white rounded'>
                <strong>Detalhes da Reserva:</strong>
                {contentData.selectedTime && (
                  <div>• Horário selecionado: {contentData.selectedTime}</div>
                )}
                {contentData.weatherConcerns && <div>• Preocupações climáticas: Sim</div>}
              </div>
            )}
          </div>
        )

      case TypeReservationBooking.EXPERIENCE:
        itemData = reservation.hotelExperience
        return (
          <div className='border rounded p-3 bg-light mb-3'>
            <h6 className='fw-bold text-success mb-3'>
              <i className='fas fa-heart me-2'></i>Detalhes da Experiência
            </h6>
            {itemData && (
              <div className='row'>
                <div className='col-md-6'>
                  <div className='mb-2'>
                    <strong>Título:</strong> {itemData.title}
                  </div>
                  <div className='mb-2'>
                    <strong>Duração:</strong> {itemData.duration}
                  </div>
                  <div className='mb-2'>
                    <strong>Tipo:</strong> {itemData.type}
                  </div>
                  {/* <div className='mb-2'>
                    <strong>Rating:</strong> ⭐ {itemData.rating}
                  </div> */}
                </div>
                <div className='col-md-6'>
                  <div className='mb-2'>
                    <strong>Status:</strong> {itemData.status ? 'Ativo' : 'Inativo'}
                  </div>
                </div>
              </div>
            )}
            {/* <div className='mb-2'>
              <strong>Descrição:</strong> {itemData?.description}
            </div> */}

            {itemData?.includes && (
              <div className='mb-3'>
                <strong>Inclui:</strong>
                <ul className='mb-0 mt-1'>
                  {itemData.includes.map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {contentData && (
              <div className='mt-3 p-2 bg-white rounded'>
                <strong>Detalhes da Reserva:</strong>
                {contentData.preferredDate && (
                  <div>• Data preferida: {toDate(contentData.preferredDate)}</div>
                )}
                {contentData.selectedSchedule && (
                  <div>• Horário selecionado: {contentData.selectedSchedule}</div>
                )}
                {contentData.timeSlot && (
                  <div>
                    • Período:{' '}
                    {contentData.timeSlot === 'morning'
                      ? 'Manhã'
                      : contentData.timeSlot === 'afternoon'
                      ? 'Tarde'
                      : contentData.timeSlot === 'evening'
                      ? 'Noite'
                      : contentData.timeSlot}
                  </div>
                )}
                {contentData.specialOccasion && (
                  <div>
                    • Ocasião especial:{' '}
                    {contentData.specialOccasion === 'honeymoon'
                      ? 'Lua de Mel'
                      : contentData.specialOccasion === 'anniversary'
                      ? 'Aniversário de Casamento'
                      : contentData.specialOccasion === 'birthday'
                      ? 'Aniversário'
                      : contentData.specialOccasion === 'proposal'
                      ? 'Pedido de Casamento'
                      : contentData.specialOccasion === 'not_applicable'
                      ? 'Não se aplica'
                      : contentData.specialOccasion}
                  </div>
                )}
                {contentData.photographyRequested !== undefined && (
                  <div>
                    • Fotografia solicitada: {contentData.photographyRequested ? 'Sim' : 'Não'}
                  </div>
                )}
                {contentData.customizationNotes && (
                  <div>• Notas de customização: {contentData.customizationNotes}</div>
                )}
                {contentData.specialRequests && (
                  <div>• Pedidos especiais: {contentData.specialRequests}</div>
                )}
                {contentData.additionalServices && contentData.additionalServices.length > 0 && (
                  <div>
                    • Serviços adicionais:{' '}
                    {contentData.additionalServices
                      .map((service: string) => {
                        const serviceLabels: Record<string, string> = {
                          flowers: 'Flores',
                          champagne: 'Champagne',
                          cake: 'Bolo',
                          music: 'Música',
                          decoration: 'Decoração',
                          photography: 'Fotografia',
                        }
                        return serviceLabels[service] || service
                      })
                      .join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
        )

      case TypeReservationBooking.ACTIVITY:
        itemData = reservation.hotelActivity
        return (
          <div className='border rounded p-3 bg-light mb-3'>
            <h6 className='fw-bold text-info mb-3'>
              <i className='fas fa-dumbbell me-2'></i>Detalhes da Atividade
            </h6>
            {itemData && (
              <div className='row'>
                <div className='col-md-6'>
                  <div className='mb-2'>
                    <strong>Título:</strong> {itemData.title}
                  </div>
                  <div className='mb-2'>
                    <strong>Horário:</strong> {itemData.time}
                  </div>
                  <div className='mb-2'>
                    <strong>Local:</strong> {itemData.location}
                  </div>
                  <div className='mb-2'>
                    <strong>Capacidade:</strong> {itemData.capacity}
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='mb-2'>
                    <strong>Categoria:</strong> {itemData.category}
                  </div>
                  <div className='mb-2'>
                    <strong>Faixa Etária:</strong> {itemData.ageGroup}
                  </div>
                  <div className='mb-2'>
                    <strong>Requer Reserva:</strong> {itemData.requiresReservation ? 'Sim' : 'Não'}
                  </div>
                  <div className='mb-2'>
                    <strong>Status:</strong> {itemData.isActive ? 'Ativo' : 'Inativo'}
                  </div>
                </div>
              </div>
            )}
            {/* <div className='mb-2'>
              <strong>Descrição:</strong> {itemData?.description}
            </div> */}

            {contentData && (
              <div className='mt-3 p-2 bg-white rounded'>
                <strong>Detalhes da Reserva:</strong>
                {contentData.sessionTime && (
                  <div>
                    • {i18n.t('orders_room_service.scheduled_date')}: {contentData.sessionTime}
                  </div>
                )}
                {contentData.skillLevel && (
                  <div>
                    • Nível de habilidade:{' '}
                    {contentData.skillLevel === 'beginner'
                      ? 'Iniciante'
                      : contentData.skillLevel === 'intermediate'
                      ? 'Intermediário'
                      : 'Avançado'}
                  </div>
                )}
              </div>
            )}
          </div>
        )

      case TypeReservationBooking.ROOMSERVICE:
        return (
          <div className='border rounded p-3 bg-light mb-3'>
            <h6 className='fw-bold text-warning mb-3'>
              <i className='fas fa-concierge-bell me-2'></i>Detalhes do Serviço de Quarto
            </h6>
            <div className='mb-2'>
              <strong>Tipo de Serviço:</strong> Serviço de Quarto
            </div>
            <div className='mb-2'>
              <strong>Item ID:</strong> {reservation.itemId}
            </div>

            {contentData && (
              <div className='mt-3 p-2 bg-white rounded'>
                <strong>Detalhes da Reserva:</strong>
                <pre className='mt-2 mb-0' style={{fontSize: '0.875rem'}}>
                  {JSON.stringify(contentData, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )

      default:
        return (
          <div className='alert alert-info'>
            <i className='fas fa-info-circle me-2'></i>
            Detalhes específicos não disponíveis para este tipo de reserva.
          </div>
        )
    }
  }

  const exportToExcel = async () => {
    setLoadingExport(true)
    try {
      const dataToExport = formatReservationBookingData(reservationBookings).map((item) => {
        // Dados base
        const baseData: any = {
          'ID Pedido': item.id,
          'ID Reserva Hotel': item.reservationId,
          'ID Quarto': item.roomId,
          Cliente: item.customerName,
          'Email Cliente': item.customerEmail,
          'CPF/Doc Cliente': item.customerDoc,
          Tipo: item.itemTypeLabel,
          Status: item.statusLabel,
          'Data Reserva': new Date(item.reservationDate).toLocaleDateString('pt-BR'),
          Participantes: item.participants,
          'Valor Total': `${item.currency} ${item.totalAmount}`,
        }

        // Adicionar dados específicos do item (novo campo "item" da API)
        if (item.item) {
          baseData['Título'] = item.item.title || '-'

          if (item.itemType === TypeReservationBooking.TOUR) {
            baseData['Duração'] = item.item.duration || '-'
            baseData['Preço'] = item.item.price ? `${item.currency} ${item.item.price}` : '-'
            baseData['Dificuldade'] = item.item.difficulty || '-'
          } else if (item.itemType === TypeReservationBooking.EXPERIENCE) {
            baseData['Duração'] = item.item.duration || '-'
            baseData['Preço'] = item.item.price ? `${item.currency} ${item.item.price}` : '-'
            baseData['Tipo Experiência'] = item.item.type || '-'
            if (item.item.includes && Array.isArray(item.item.includes)) {
              baseData['Inclui'] = item.item.includes.join(', ')
            }
          } else if (item.itemType === TypeReservationBooking.ACTIVITY) {
            baseData['Local'] = item.item.location || '-'
            baseData['Categoria'] = item.item.category || '-'
          }
        }

        // Parse do content se existir
        try {
          const contentData = item.content ? JSON.parse(item.content) : null
          if (contentData) {
            if (contentData.preferredDate) {
              baseData['Data Preferida'] = new Date(contentData.preferredDate).toLocaleDateString(
                'pt-BR'
              )
            }
            if (contentData.selectedSchedule) {
              baseData['Horário Selecionado'] = contentData.selectedSchedule
            }
            if (contentData.sessionTime) {
              baseData['Horário Sessão'] = contentData.sessionTime
            }
            if (contentData.timeSlot) {
              baseData['Período'] =
                contentData.timeSlot === 'morning'
                  ? 'Manhã'
                  : contentData.timeSlot === 'afternoon'
                  ? 'Tarde'
                  : contentData.timeSlot === 'evening'
                  ? 'Noite'
                  : contentData.timeSlot
            }
            if (contentData.specialOccasion) {
              const occasions: any = {
                birthday: 'Aniversário',
                anniversary: 'Aniversário de Casamento',
                honeymoon: 'Lua de Mel',
                proposal: 'Pedido de Casamento',
                not_applicable: 'Não se aplica',
              }
              baseData['Ocasião Especial'] =
                occasions[contentData.specialOccasion] || contentData.specialOccasion
            }
            if (contentData.weatherConcerns !== undefined) {
              baseData['Preocupações Climáticas'] = contentData.weatherConcerns ? 'Sim' : 'Não'
            }
            if (contentData.skillLevel) {
              baseData['Nível Habilidade'] =
                contentData.skillLevel === 'beginner'
                  ? 'Iniciante'
                  : contentData.skillLevel === 'intermediate'
                  ? 'Intermediário'
                  : 'Avançado'
            }
            if (contentData.photographyRequested !== undefined) {
              baseData['Fotografia Solicitada'] = contentData.photographyRequested ? 'Sim' : 'Não'
            }
            if (contentData.pickupLocation) {
              baseData['Local de Embarque'] = contentData.pickupLocation
            }
            if (contentData.emergencyContactName) {
              baseData['Contato Emergência'] = contentData.emergencyContactName
            }
            if (contentData.emergencyContactPhone) {
              baseData['Tel. Emergência'] = contentData.emergencyContactPhone
            }
            if (contentData.dietaryRestrictions && Array.isArray(contentData.dietaryRestrictions)) {
              baseData['Restrições Alimentares'] = contentData.dietaryRestrictions.join(', ')
            }
            if (contentData.equipmentNeeded !== undefined) {
              baseData['Equipamento Necessário'] = contentData.equipmentNeeded ? 'Sim' : 'Não'
            }
            if (contentData.additionalServices && Array.isArray(contentData.additionalServices)) {
              baseData['Serviços Adicionais'] = contentData.additionalServices.join(', ')
            }
          }
        } catch (e) {
          // Ignora erro de parse
        }

        baseData['Solicitações Especiais'] = item.specialRequests || '-'
        baseData['Observações Hóspede'] = item.guestNotes || '-'
        if (item.cancelled) {
          baseData['Data Cancelamento'] = new Date(item.cancelled).toLocaleString('pt-BR')
          baseData['Motivo Cancelamento'] = item.cancellationReason || '-'
        }
        baseData['Data Criação'] = item.created

        return baseData
      })

      const ws = XLSX.utils.json_to_sheet(dataToExport)

      // Ajustar largura das colunas automaticamente
      const maxWidth = 50
      const colWidths = Object.keys(dataToExport[0] || {}).map((key) => ({
        wch: Math.min(Math.max(key.length, 12), maxWidth),
      }))
      ws['!cols'] = colWidths

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Pedidos')

      const timestamp = new Date().toISOString().split('T')[0]
      XLSX.writeFile(wb, `pedidos-atividades-${timestamp}.xlsx`)

      await Swal.fire({
        icon: 'success',
        title: String('Exportado com sucesso!'),
        text: String('O arquivo Excel foi baixado.'),
        confirmButtonText: String('OK'),
      })
    } catch (error) {
      console.error('Erro ao exportar:', error)
      await Swal.fire({
        icon: 'error',
        title: String('Erro ao exportar'),
        text: String('Ocorreu um erro ao gerar o arquivo Excel.'),
        confirmButtonText: String('OK'),
      })
    } finally {
      setLoadingExport(false)
    }
  }

  useEffect(() => {
    getReservationBookings()
    getStatusHotel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('permissions.orders_activities')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Gerenciamento de reservas de tours, atividades e experiências
                </span>
              </h3>
            </div>
            <div className='card-body border-bottom'>
              <div className='row g-3'>
                <div className='col-md-3'>
                  <Input
                    label='Tipo de Item'
                    inputAttr={{
                      type: 'select',
                      placeholder: 'Selecione o tipo',
                    }}
                    value={filters.itemType || ''}
                    change={(value: TypeReservationBooking) =>
                      handleFilterChange('itemType', value || undefined)
                    }
                    options={[
                      {value: '', select: 'Todos os tipos'},
                      {value: TypeReservationBooking.TOUR, select: 'Tour'},
                      {value: TypeReservationBooking.EXPERIENCE, select: 'Experiência'},
                      {value: TypeReservationBooking.ACTIVITY, select: 'Atividade'},
                      {value: TypeReservationBooking.ROOMSERVICE, select: 'Serviço de Quarto'},
                    ]}
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label='Status'
                    inputAttr={{
                      type: 'select',
                      placeholder: 'Selecione o status',
                    }}
                    value={selectedStatusSlug}
                    change={(value: string) =>
                      handleFilterChange(
                        'statusHotelId',
                        statusHotelOptions.find((option) => option.slug === value)?.id || undefined
                      )
                    }
                    options={[
                      {value: '', select: 'Todos os status'},
                      ...statusOptions,
                    ]}
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label='Data Inicial'
                    inputAttr={{
                      type: 'date',
                      placeholder: 'Data inicial',
                    }}
                    value={filters.startDate || ''}
                    change={(value: string) => handleFilterChange('startDate', value || undefined)}
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label='Data Final'
                    inputAttr={{
                      type: 'date',
                      placeholder: 'Data final',
                    }}
                    value={filters.endDate || ''}
                    change={(value: string) => handleFilterChange('endDate', value || undefined)}
                  />
                </div>

                <div className='col-12 d-flex justify-content-end'>
                  <div className='d-flex gap-2'>
                    <Button text='Pesquisar' color='primary' size='small' click={handleSearch} />
                    <Button
                      text='Limpar Filtros'
                      color='danger'
                      size='small'
                      click={handleClearFilters}
                    />
                    <Button
                      text='Exportar Excel'
                      color='success'
                      size='small'
                      click={exportToExcel}
                      icon='fas fa-download'
                      loading={loadingExport}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='card-body'>
              <Datatable
                data={formatReservationBookingData(reservationBookings)}
                headers={[
                  {key: 'Título', value: 'title', type: 'default'},
                  {key: 'Tipo', value: 'itemTypeLabel', type: 'default'},
                  {key: 'Status', value: 'statusLabel', type: 'default'},
                  {key: 'Data', value: 'created', type: 'default'},
                  {key: 'Participantes', value: 'participantsText', type: 'default'},
                  {key: 'Valor Total', value: 'totalAmountFormatted', type: 'default'},
                  {key: 'Ações', value: 'acoes', type: ''},
                ]}
                options={[
                  {
                    icon: 'fas fa-eye',
                    action: (item) => {
                      getReservationDetails(item.id)
                    },
                    title: 'Ver Detalhes',
                  },
                  {
                    icon: 'fas fa-edit',
                    action: (item) => {
                      showStatusModal(item)
                    },
                    title: 'Alterar Status',
                  },
                  {
                    icon: 'fas fa-ban',
                    action: (item) => {
                      showCancelModal(item)
                    },
                    title: 'Cancelar Reserva',
                  },
                ]}
                color='primary'
                meta={meta}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal para visualizar detalhes */}
      {modalType === 'details' && (
        <ModalWrapper
          confirmAction={() => {
            closeWrapperModal()
            setModalType(null)
            setSelectedReservation(null)
          }}
          textAction='Fechar'
          noCloseButton={true}
          confirmButtomColor='secondary'
        >
          {loadingDetails ? (
            <div className='text-center p-4'>
              <div className='spinner-border' role='status'>
                <span className='sr-only'>Carregando...</span>
              </div>
            </div>
          ) : selectedReservation ? (
            <div className='p-3'>
              {/* Informações Básicas da Reserva */}
              <div className='border rounded p-3 mb-3'>
                <h6 className='fw-bold mb-3'>
                  <i className='fas fa-calendar-check me-2'></i>Informações da Reserva
                </h6>
                <div className='row'>
                  <div className='col-md-6'>
                    <div className='mb-2'>
                      <strong>ID da Reserva:</strong> #{selectedReservation.id}
                    </div>
                    <div className='mb-2'>
                      <strong>ID Hotel Reserva:</strong> #{selectedReservation.reservationId}
                    </div>
                    <div className='mb-2'>
                      <strong>Tipo:</strong> {getItemTypeLabel(selectedReservation.itemType)}
                    </div>
                    <div className='mb-2'>
                      <strong>Status:</strong>
                      <span
                        className={`ms-2 badge ${getStatusBadgeClass(
                          getReservationStatusSlug(selectedReservation)
                        )}`}
                      >
                        {getReservationStatusName(selectedReservation)}
                      </span>
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='mb-2'>
                      <strong>Data da Reserva:</strong>{' '}
                      {selectedReservation.itemType === TypeReservationBooking.EXPERIENCE
                        ? new Date(selectedReservation.reservationDate).toLocaleDateString('pt-BR')
                        : new Date(selectedReservation.reservationDate).toLocaleString('pt-BR')}
                    </div>
                    <div className='mb-2'>
                      <strong>Participantes:</strong> {selectedReservation.participants} pessoa
                      {selectedReservation.participants > 1 ? 's' : ''}
                    </div>
                    <div className='mb-2'>
                      <strong>Valor Total:</strong> {selectedReservation.currency}{' '}
                      {selectedReservation.totalAmount}
                    </div>
                    <div className='mb-2'>
                      <strong>Criado em:</strong>{' '}
                      {new Date(selectedReservation.created).toLocaleString('pt-BR')}
                    </div>
                  </div>
                </div>

                {selectedReservation.address && (
                  <div className='mt-2'>
                    <strong>Endereço:</strong> {selectedReservation.address}
                  </div>
                )}
              </div>

              {/* Detalhes Específicos do Item */}
              {renderItemDetails(selectedReservation)}

              {/* Informações da Reserva do Hotel */}
              {selectedReservation.hotelReservation && (
                <div className='border rounded p-3 mb-3'>
                  <h6 className='fw-bold mb-3'>
                    <i className='fas fa-hotel me-2'></i>Reserva do Hotel
                  </h6>
                  <div className='row'>
                    <div className='col-md-6'>
                      <div className='mb-2'>
                        <strong>Check-in:</strong>{' '}
                        {new Date(selectedReservation.hotelReservation.checkIn).toLocaleString(
                          'pt-BR'
                        )}
                      </div>
                      <div className='mb-2'>
                        <strong>Quarto ID:</strong> {selectedReservation.hotelReservation.roomId}
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='mb-2'>
                        <strong>Check-out:</strong>{' '}
                        {selectedReservation.hotelReservation.checkOut
                          ? new Date(selectedReservation.hotelReservation.checkOut).toLocaleString(
                              'pt-BR'
                            )
                          : 'Em aberto'}
                      </div>
                      <div className='mb-2'>
                        <strong>Cliente ID:</strong>{' '}
                        {selectedReservation.hotelReservation.customerId}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Solicitações e Observações */}
              {(selectedReservation.specialRequests || selectedReservation.guestNotes) && (
                <div className='border rounded p-3 mb-3'>
                  <h6 className='fw-bold mb-3'>
                    <i className='fas fa-comment-dots me-2'></i>Observações
                  </h6>
                  {selectedReservation.specialRequests && (
                    <div className='mb-2'>
                      <strong>Solicitações Especiais:</strong>
                      <p className='mb-0 mt-1'>{selectedReservation.specialRequests}</p>
                    </div>
                  )}
                  {selectedReservation.guestNotes && (
                    <div className='mb-2'>
                      <strong>Observações do Hóspede:</strong>
                      <p className='mb-0 mt-1'>{selectedReservation.guestNotes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Informações de Cancelamento */}
              {selectedReservation.cancelled && (
                <div className='border border-danger rounded p-3 bg-light-danger'>
                  <h6 className='fw-bold text-danger mb-3'>
                    <i className='fas fa-ban me-2'></i>Informações de Cancelamento
                  </h6>
                  <div className='mb-2'>
                    <strong>Data do Cancelamento:</strong>
                    <span className='text-danger ms-2'>
                      {new Date(selectedReservation.cancelled).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  {selectedReservation.cancellationReason && (
                    <div className='mb-2'>
                      <strong>Motivo:</strong>
                      <span className='text-danger ms-2'>
                        {selectedReservation.cancellationReason}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </ModalWrapper>
      )}

      {/* Modal para cancelar reserva */}
      {modalType === 'cancel' &&
        selectedReservation &&
        !isReservationCancelled(selectedReservation) && (
          <ModalWrapper
            confirmAction={cancelReservation}
            textAction='Cancelar Reserva'
            noCloseButton={false}
            confirmButtomColor='danger'
            disableMainButton={loadingCancel || !cancelReason.trim()}
            onCloseAction={() => {
              setModalType(null)
              setSelectedReservation(null)
              setCancelReason('')
            }}
          >
            <div className='p-3'>
              <div className='alert alert-warning'>
                <i className='fas fa-exclamation-triangle me-2'></i>
                <strong>Atenção:</strong> Esta ação não pode ser desfeita. A reserva será cancelada
                permanentemente.
              </div>

              <div className='mb-4'>
                <h6 className='fw-bold'>Detalhes da Reserva:</h6>
                <p className='mb-1'>
                  <strong>Tipo:</strong>{' '}
                  {typeLabels[selectedReservation.itemType as keyof typeof typeLabels] ||
                    selectedReservation.itemType}
                </p>
                <p className='mb-1'>
                  <strong>Data:</strong>{' '}
                  {new Date(selectedReservation.reservationDate).toLocaleString('pt-BR')}
                </p>
                <p className='mb-1'>
                  <strong>Participantes:</strong> {selectedReservation.participants}
                </p>
                <p className='mb-1'>
                  <strong>Valor:</strong> {selectedReservation.currency}{' '}
                  {selectedReservation.totalAmount}
                </p>
              </div>

              <Input
                label='Motivo do Cancelamento *'
                inputAttr={{
                  type: 'select',
                  placeholder: 'Selecione o motivo do cancelamento',
                  required: true,
                }}
                value={cancelReason}
                change={(value: string) => setCancelReason(value)}
                options={[
                  {value: '', select: 'Selecione um motivo'},
                  ...(
                    reasonsByType[selectedReservation.itemType as keyof typeof reasonsByType] || []
                  ).map((reason) => ({
                    value: reason,
                    select: reason,
                  })),
                ]}
              />

              {loadingCancel && (
                <div className='text-center mt-3'>
                  <div className='spinner-border spinner-border-sm' role='status'>
                    <span className='sr-only'>Processando...</span>
                  </div>
                  <span className='ms-2'>Cancelando reserva...</span>
                </div>
              )}
            </div>
          </ModalWrapper>
        )}

      {/* Modal para alterar status */}
      {modalType === 'status' && selectedReservation && (
        <ModalWrapper
          confirmAction={changeReservationStatus}
          textAction='Alterar Status'
          noCloseButton={false}
          confirmButtomColor='primary'
          disableMainButton={loadingStatusChange || !newStatus}
          onCloseAction={() => {
            setModalType(null)
            setSelectedReservation(null)
            setNewStatus('')
          }}
        >
          <div className='p-3'>
            <div className='mb-4'>
              <h6 className='fw-bold'>Reserva #{selectedReservation.id}</h6>
              <p className='mb-1'>
                <strong>Tipo:</strong> {getItemTypeLabel(selectedReservation.itemType)}
              </p>
              <p className='mb-1'>
                <strong>Status atual:</strong>
                <span
                  className={`ms-2 badge ${getStatusBadgeClass(
                    getReservationStatusSlug(selectedReservation)
                  )}`}
                >
                  {getReservationStatusName(selectedReservation)}
                </span>
              </p>
            </div>

            <Input
              label='Novo Status'
              inputAttr={{
                type: 'select',
                placeholder: 'Selecione o novo status',
                required: true,
              }}
              value={newStatus}
              change={(value: string) => setNewStatus(value)}
              options={[
                {value: '', select: 'Selecione um status'},
                ...statusOptions,
              ]}
              required={true}
            />
          </div>
        </ModalWrapper>
      )}
    </>
  )
}

const OrdersActivities: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {title: 'Reservas de Serviços', path: 'tours-reservations', isActive: true},
        ]}
      >
        Reservas de Serviços
      </PageTitle>
      <HotelReservesContent />
    </>
  )
}

export {OrdersActivities}
